import { createRouter, createWebHistory } from 'vue-router'

import { isSimpleMode } from '@/modules/simpleMode'
import { evaluateRouteAccess, firstAccessiblePath } from '@/app/access'
import { useAuthStore } from '@/modules/auth/store'
import LoginPage from '@/modules/auth/LoginPage.vue'
import ChangePasswordPage from '@/modules/auth/ChangePasswordPage.vue'
import AppShell from '@/shared/components/AppShell.vue'
import RouteResultPage from '@/shared/components/RouteResultPage.vue'

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', name: 'login', component: LoginPage, meta: { public: true } },
    { path: '/change-password', name: 'change-password', component: ChangePasswordPage },
    {
      path: '/',
      name: 'home',
      component: AppShell,
      children: [
        { path: 'records/transfers', name: 'native-transfers', meta: { simpleEntityKey: 'transfers' }, component: isSimpleMode()
          ? () => import('@/modules/EntityPage.vue') : () => import('@/modules/inventory/TransferWorkbench.vue') },
        { path: 'records/physical-transfers', name: 'physical-transfers',
          component: () => import('@/modules/inventory/TransferWorkbench.vue') },
        { path: 'records/stock_takes', name: 'native-stocktakes', meta: { simpleEntityKey: 'stock_takes' }, component: isSimpleMode()
          ? () => import('@/modules/EntityPage.vue') : () => import('@/modules/inventory/StockTakeWorkbench.vue') },
        { path: 'records/physical-stocktakes', name: 'physical-stocktakes',
          component: () => import('@/modules/inventory/StockTakeWorkbench.vue') },
        { path: 'records/scrap_orders', name: 'native-scraps', meta: { simpleEntityKey: 'scrap_orders' }, component: isSimpleMode()
          ? () => import('@/modules/EntityPage.vue') : () => import('@/modules/inventory/ScrapWorkbench.vue') },
        { path: 'records/physical-scraps', name: 'physical-scraps',
          component: () => import('@/modules/inventory/ScrapWorkbench.vue') },
        { path: 'records/production_orders', name: 'processing-orders', component: () => import('@/modules/processing/ProcessingWorkbench.vue') },
        { path: 'sales/freights', name: 'sales-freights', component: () => import('@/modules/sales/FreightWorkbench.vue') },
        { path: 'sales/freight-corrections', name: 'sales-freight-corrections', component: () => import('@/modules/sales/FreightCorrectionWorkbench.vue') },
        { path: 'sales/after-sales', name: 'sales-after-sales', component: () => import('@/modules/sales/AfterSalesWorkbench.vue') },
        { path: 'sales/after-sale-returns', name: 'sales-after-sale-returns', component: () => import('@/modules/sales/AfterSalesReturnWorkbench.vue') },
        { path: 'sales/after-sale-replacements', name: 'sales-after-sale-replacements', component: () => import('@/modules/sales/AfterSalesReplacementWorkbench.vue') },
        { path: 'finance/supplier-refunds', name: 'supplier-refunds', component: () => import('@/modules/finance/SupplierRefundPage.vue') },
        { path: 'finance/customer-money', name: 'customer-money', component: () => import('@/modules/finance/CustomerMoneyPage.vue') },
        { path: 'finance/customer-refunds', name: 'customer-refunds', component: () => import('@/modules/finance/CustomerRefundPage.vue') },
        { path: 'finance/customer-statement', name: 'customer-statement', component: () => import('@/modules/finance/CustomerStatementPage.vue') },
        { path: 'finance/supplier-credits', name: 'supplier-credits', component: () => import('@/modules/finance/SupplierCreditPage.vue') },
        { path: 'procurement/supplier-returns', name: 'supplier-returns', component: () => import('@/modules/procurement/SupplierReturnWorkbench.vue') },
        { path: 'procurement/landed-costs', name: 'landed-costs', component: () => import('@/modules/procurement/LandedCostWorkbench.vue') },
        { path: 'records/:entityKey', name: 'records', component: () => import('@/modules/EntityPage.vue') },
        ...(['period-inventory', 'profitability', 'stock-analysis', 'scrap-loss'] as const).map(kind => ({
          path: `reports/${kind}`, name: `reports-${kind}`,
          component: () => import('@/modules/reporting/ReportingWorkbench.vue'), props: { kind },
        })),
        { path: 'reports/inout', redirect: to => ({path:'/reports/period-inventory',query:to.query}) },
        { path: 'reports/gross-margin', redirect: to => ({path:'/reports/profitability',query:to.query}) },
        { path: 'reports/location-map', name: 'reports-location-map', component: () => import('@/modules/reporting/LocationMapPage.vue') },
        { path: 'settings/inventory-analysis', name: 'reporting-rules', component: () => import('@/modules/reporting/ReportingRules.vue') },
        { path: 'notifications', name: 'reporting-alerts', component: () => import('@/modules/reporting/ReminderInbox.vue') },
        { path: 'reports/:reportKey', name: 'reports', component: () => import('@/modules/ReportPage.vue') },
        { path: 'finance/supplier-invoices', name: 'supplier-invoices', component: () => import('@/modules/finance/SupplierInvoiceWorkbench.vue') },
        { path: 'finance/payment-instructions', name: 'payment-instructions', component: () => import('@/modules/finance/PaymentWorkbench.vue') },
        { path: 'finance/supplier-activity', name: 'supplier-activity', component: () => import('@/modules/finance/SupplierActivityPage.vue') },
        { path: 'approvals', name: 'approvals', component: () => import('@/modules/ApprovalPage.vue') },
        { path: 'settings/approval-flows', name: 'approval-flows',
          component: () => import('@/modules/ApprovalFlowPage.vue') },
        { path: 'settings/roles', name: 'role-permissions',
          component: () => import('@/modules/accounts/RolePermissionPage.vue') },
        { path: 'settings/users', name: 'user-admin',
          component: () => import('@/modules/accounts/UserAdminPage.vue') },
        { path: 'settings/document-numbers', name: 'document-numbering',
          component: () => import('@/modules/accounts/DocumentNumberingPage.vue') },
        { path: 'forbidden', name: 'forbidden', component: RouteResultPage,
          props: { status: '403', titleKey: 'route.forbiddenTitle', subtitleKey: 'route.forbiddenSubtitle' } },
        { path: 'not-found', name: 'not-found', component: RouteResultPage,
          props: { status: '404', titleKey: 'route.notFoundTitle', subtitleKey: 'route.notFoundSubtitle' } },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: { name: 'not-found' } },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  await auth.restore()
  if (!to.meta.public && !auth.isAuthenticated) return { name: 'login', query: { next: to.fullPath } }
  // Forced change-password gate paused for business rollout.
  if (to.name === 'login' && auth.isAuthenticated) return { path: firstAccessiblePath(auth.can) }
  if (to.name === 'home') return { path: firstAccessiblePath(auth.can) }
  const access = evaluateRouteAccess({
    name: to.name,
    entityKey: typeof to.params.entityKey === 'string' ? to.params.entityKey : undefined,
    reportKey: typeof to.params.reportKey === 'string' ? to.params.reportKey : undefined,
    isStaff: Boolean(auth.user?.is_staff),
  }, auth.can)
  if (access === 'forbidden' && to.name !== 'forbidden') return { name: 'forbidden' }
  if (access === 'not-found' && to.name !== 'not-found') return { name: 'not-found' }
  return true
})
